/**
 * Mission College brand colors (W54 Worker C, S3 — theme).
 *
 * Source, ONE first-party, `reported` (doc 51 S0-7 wants two; the college's own brand guide was
 * not probed at S0 — fact sheet open item; driver ruling 2026-10-21: the eLumen CSS-var colors
 * stay `reported`, as EVC's brand item): the catalog theme CSS variables that every publish-API
 * body carries (admin_tools/backend/data/onboarding/mission-college/probes/PROBES.md §9):
 *   --color-theme1 / --color-header / --color-footer #097799 (teal),
 *   --color-theme2 #EA730B (orange), --color-theme3 #FFCB05 (yellow), --color-theme4 #003762
 *   (navy), --color-theme5 #643091, --color-theme6 #C1F200, --color-theme-dark #191e22.
 * The header/footer teal is the primary; the orange is the accent hue.
 *
 * Contrast (WCAG, measured 2026-10-21 against the token surfaces in index.css, scratchpad
 * theme_colors.py):
 *   Teal #097799: 5.11:1 on the card #FFFFFF, 4.54:1 on paper #F5F1EB; white text on it 5.11:1.
 *     `-strong` = the teal darkened until white text clears 7:1: #075F7A (7.17:1).
 *   Orange #EA730B fails as TEXT on light surfaces (3.02 / 2.68), and `text-accent` renders at
 *     12 px in the audit, so 4.5:1 applies. The accent TOKEN is the orange's own hue darkened
 *     until it clears 4.5 on BOTH light surfaces: #AD5508 = 5.15:1 on the card, 4.57:1 on paper.
 *     On the dark surfaces it measures 3.57:1 (#161412) / 3.29:1 (#1F1C19) — the 3:1 floor only;
 *     the theme system has no dark-mode accent token (doc 51 S3-6; De Anza, SDSU and Foothill
 *     record the same). The bright orange lives in the -300/-400 tints for fills.
 * Ramps are HSL ladders pinned at the brand values (the catalog publishes no extended palette).
 * Text-only branding, no logo asset embedded.
 *
 * Brand mark (components/ui/BrandMark.tsx paints three faces from `--color-mark-*`; a theme
 * without them inherits UMD's red from index.css — found missing 2026-10-21 by the registry
 * completeness test after the rebase onto #469). The cross-theme distance floor was retired
 * (owner decision 2026-10-14), so the mark follows Mission's own teal, taken from the ladder
 * above so no new colour is introduced — UMD's shape, each face one step darker:
 *   light: front primary-500 (9 119 153), top primary-600 (7 95 122), side primary-700 (6 74 95);
 *   dark:  front primary-300 (53 198 243), top primary-400 (13 176 226), side primary-500.
 * Light front on paper #F5F1EB 4.54:1 and dark front on #161412 9.21:1, both above the 3:1
 * non-text floor.
 */
import type { ThemeColors } from '../types';

export const missionCollegeColors: ThemeColors = {
  // Primary — Mission teal (#097799 = rgb(9, 119, 153)); -strong = #075F7A.
  '--color-primary':     '9 119 153',
  '--color-primary-strong': '7 95 122',
  '--color-primary-50': '236 250 254',
  '--color-primary-100': '188 236 251',
  '--color-primary-200': '120 217 247',
  '--color-primary-300': '53 198 243',
  '--color-primary-400': '13 176 226',
  '--color-primary-500': '9 119 153',
  '--color-primary-600': '7 95 122',
  '--color-primary-700': '6 74 95',
  '--color-primary-800': '4 52 67',
  '--color-primary-900': '2 32 41',

  // Accent — Mission orange hue, light-mode TEXT tier (#AD5508 = rgb(173, 85, 8)); see header.
  '--color-accent':     '173 85 8',
  '--color-accent-50': '254 244 236',
  '--color-accent-100': '252 217 187',
  '--color-accent-200': '249 179 119',
  '--color-accent-300': '246 141 50',
  '--color-accent-400': '229 113 11',
  '--color-accent-500': '173 85 8',
  '--color-accent-600': '138 68 6',
  '--color-accent-700': '107 53 5',
  '--color-accent-800': '76 37 4',
  '--color-accent-900': '47 23 2',

  // Brand-mark faces (see header): teal ladder steps, each face darker; dark mode one tint lighter.
  '--color-mark-front': '9 119 153',
  '--color-mark-top':   '7 95 122',
  '--color-mark-side':  '6 74 95',
  '--color-mark-front-dm': '53 198 243',
  '--color-mark-top-dm':   '13 176 226',
  '--color-mark-side-dm':  '9 119 153',
};
